<template>
  <div class="container">
    <h1>API Credit Consumption</h1>
    <p class="description">
      Each API call below consumes credits based on its computational complexity.
      <br/>      
      Click an endpoint to view full documentation in Swagger UI.
    </p>

    <div v-for="section in groupedCredits" :key="section.title">
      <h2>{{ section.title }}</h2>
      <table>
        <thead>
          <tr>
            <th class="col-path">API Endpoint</th>
            <th class="col-credit">Credits</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in section.entries" :key="entry.path">
            <td>
              <a
                :href="`https://api.vedicastrochart.com/swagger-ui/index.html#/${entry.tag.replaceAll(' ', '%20')}/${entry.operationId}`"
                target="_blank"
              >
                {{ entry.path }}
              </a>
            </td>
            <td class="credits">{{ entry.credits }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <button class="cta-button" @click="$router.push('/')">Home</button>

  </div>
</template>

<script setup>
const groupedCredits = [
  {
    title: 'Ayanamsha Identifiers',
    entries: [
      {
        path: '/v1/public/options/ayanamshas',
        credits: 0.0,
        tag: 'Ayanamsha List',
        operationId: 'getAyanamshaList'
      }
    ]
  },
  {
    title: 'City Search - Birth Place',
    entries: [
      {
        path: '/v1/public/geo/birth-city-search',
        credits: 0.1,
        tag: 'City Search - Birth Place',
        operationId: 'getBirthCitySearchResults'
      }
    ]
  },
  {
    title: 'City Search - Generic',
    entries: [
      {
        path: '/v1/public/geo/city-search',
        credits: 0.1,
        tag: 'City Search - Generic',
        operationId: 'getGenericCitySearchResults'
      }
    ]
  },
  {
    title: 'Astrology Concepts',
    entries: [
      { path: '/v1/public/astro/planetary-positions', credits: 1.0, tag: 'Planetary Positions', operationId: 'getPlanetaryPositions' },
      { path: '/v1/public/astro/current-planetary-positions', credits: 1.0, tag: 'Current Planetary Positions', operationId: 'getCurrentPlanetaryPositions' },
      { path: '/v1/public/astro/housecusps/placidus', credits: 1.0, tag: 'Placidus House Cusps', operationId: 'getHouseCuspsPlacidus' },
      { path: '/v1/public/astro/ascendant', credits: 0.25, tag: 'Ascendant', operationId: 'getAscendant' },
      { path: '/v1/public/astro/sunrise', credits: 0.25, tag: 'Sunrise', operationId: 'getSunrise' },
      { path: '/v1/public/astro/hora', credits: 0.5, tag: 'Hora', operationId: 'getHora' },
      { path: '/v1/public/astro/panchanga', credits: 1.25, tag: 'Panchanga - DateTime', operationId: 'getPanchanga' },
      { path: '/v1/public/astro/panchanga-current', credits: 1.25, tag: 'Panchanga - Current', operationId: 'getCurrentPanchanga' },
      { path: '/v1/public/astro/panchanga-cycle', credits: 5.0, tag: 'Panchanga - Cycle', operationId: 'getPanchangaEvents' },
      { path: '/v1/public/astro/divisional-chart', credits: 1.25, tag: 'Divisional Chart', operationId: 'getDivisionalChart' },
      { path: '/v1/public/astro/vimsottari-periods', credits: 1.25, tag: 'Vimsottari Periods', operationId: 'getVimsottariPeriods' },
      { path: '/v1/public/astro/vimsottari-periods-mahadasha', credits: 1.25, tag: 'Vimsottari 1st Level Mahadasha Periods', operationId: 'getMahadashaVimsottariPeriods' },
      { path: '/v1/public/astro/vimsottari-periods-antardasha', credits: 1.25, tag: 'Vimsottari 2nd Level Antardasha Periods', operationId: 'getAntarDashaVimsottariPeriods' },
      { path: '/v1/public/astro/vimsottari-periods-pratyantardasha', credits: 1.25, tag: 'Vimsottari 3rd Level Pratyantardasha Periods', operationId: 'getPratyantardashaVimsottariPeriods' },
      { path: '/v1/public/astro/sub-planet/maandi', credits: 0.75, tag: 'Sub-Planet Maandi', operationId: 'getMaandi' }
    ]
  },
  {
    title: 'Transit APIs',
    entries: [
      { path: '/v1/public/astro/transit/ascendant-degree-rising', credits: 1.0, tag: 'Transit - Ascendant Degree Rising', operationId: 'getAscendantDegreeRisingTime' },
    ]
  },
  {
    title: 'Natal Chart PDF APIs',
    entries: [
      { path: '/v1/public/astro/natal-chart-summary-pdf', credits: 25.0, tag: 'Natal Chart - Summary PDF', operationId: 'getNatalChartSummaryPDF' },
    ]
  },
  {
    title: 'Horary APIs',
    entries: [
      { path: '/v1/public/astro/horary/jamakkol', credits: 3.0, tag: 'Jamakkol Planets', operationId: 'getJamakkolPlanets' },
      { path: '/v1/public/astro/horary/kp-horary-chart', credits: 3.0, tag: 'KP Horary Chart', operationId: 'getKPHoraryChart' },
    ]
  }
];
</script>

<style scoped>
.container {
  max-width: 960px;
  margin: 0 auto;
  padding: 30px 20px;
  background: #EDF6F9;
  border-radius: 10px;
  font-family: Arial, sans-serif;
}

.description {
  text-align: center;
  margin-bottom: 30px;
  color: #555;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #222;
}

h2 {
  border-bottom: 2px solid #ddd;
  padding-bottom: 5px;
  margin-top: 40px;
  color: #444;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: fixed;
}

th, td {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  vertical-align: middle;
}

th.col-path, td.col-path {
  width: 80%;
  text-align: left;
}

th.col-credit, td.col-credit, .credits {
  width: 20%;
  text-align: center;
  font-weight: bold;
}

a {
  color: #1a73e8;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.cta-button {
  display: block;
  margin: 30px auto 0;
  background-color: #087F8C;
  color: white;
  font-weight: bold;
  border: none;
  padding: 12px 28px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
}

.cta-button:hover {
  background-color: #218838;
}

</style>
